import { EyeIcon } from '@heroicons/react/24/outline';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import QuestionsPreview from '@/components/question/preview/QuestionsPreview';
import QuestionsPreviewModal from '@/components/question/preview/QuestionsPreviewModal';
import { CourseAdminCard } from '@/components/shared/CourseAdminCard';
import {
  CardDragglableProps,
  CardStaticProps,
} from '@/components/shared/CourseAdminCard/types';
import { Badge, Button } from '@/components/ui/daisy';
import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import useToggle from '@/data/hook/useToggle';
import { Question } from '@/models/Question';
import { Breakpoints } from '@/utils/Breakpoint';
import normalize from '@/utils/statistics/normalize';
import { getTranslatableValue } from '@/utils/translatable-field/getTranslatableValue';
import QuestionTypeIcon from './QuestionTypeIcon';

type QuestionCardProps = {
  question: Question;
  onDelete?: (identifier: string) => void;
  onEdit?: (question: Question) => void;
  className?: string;
} & (CardStaticProps | CardDragglableProps);

export default function QuestionCard({
  question,
  onDelete,
  onEdit,
  ...cardProps
}: QuestionCardProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'common' });
  const { t: tPreview } = useTranslation('translation', {
    keyPrefix: 'questionsPreview',
  });
  const { isOpen: isPreviewOpen, open, close } = useToggle(false);
  const grade = useQuestionExamGradeCtx()?.getGradeForQuestion(question);
  const isXl = useMediaQuery({ minWidth: Breakpoints.xl });

  return (
    <Fragment>
      <CourseAdminCard {...cardProps}>
        <CourseAdminCard.Header>
          <CourseAdminCard.Leading>
            <CourseAdminCard.Order value={question.order} />
            <CourseAdminCard.LeadingIcon>
              <QuestionTypeIcon
                type={question.type}
                className="w-6 h-6 text-base-content"
              />
            </CourseAdminCard.LeadingIcon>
            {grade && (
              <Badge variant="soft" color="info" size={isXl ? 'sm' : 'xs'}>
                {`${normalize(grade.maxGrade, 2)}p`}
              </Badge>
            )}
            <CourseAdminCard.Title className="font-medium">
              {getTranslatableValue(question.title)}
            </CourseAdminCard.Title>
          </CourseAdminCard.Leading>
          <CourseAdminCard.Actions>
            <Button
              variant="soft"
              color="secondary"
              size="sm"
              shape="square"
              aria-label={t('preview')}
              onClick={open}
            >
              <EyeIcon className="max-xl:w-3.5 w-4" />
            </Button>
            <CourseAdminCard.EditButton onClick={() => onEdit?.(question)} />
            {onDelete && (
              <CourseAdminCard.MoreActions>
                <CourseAdminCard.MoreActions.Delete
                  onClick={() => onDelete(question.identifier)}
                />
              </CourseAdminCard.MoreActions>
            )}
          </CourseAdminCard.Actions>
        </CourseAdminCard.Header>
      </CourseAdminCard>
      <QuestionsPreviewModal visible={isPreviewOpen} onClose={close}>
        <QuestionsPreview
          questions={[question]}
          title={tPreview('questionTitle')}
        />
      </QuestionsPreviewModal>
    </Fragment>
  );
}
